'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { HOTEL_PHONE_DISPLAY, HOTEL_PHONE_TEL, HOTEL_EMAIL } from '@/lib/contact';
import { CookieSettingsLink } from './cookie-settings-link';

type FooterVariant = 'default' | 'spa';

interface FooterProps {
  variant?: FooterVariant;
}

const FOOTER_COLORS = {
  default: { primary: '#5e0e1a', secondary: '#4e0c14', tertiary: '#3c080e' },
  spa:     { primary: '#478B8B', secondary: '#356666', tertiary: '#2a5252' },
};

export function Footer({ variant = 'default' }: FooterProps) {
  const t = useTranslations('common');
  const currentYear = new Date().getFullYear();
  const scheme = FOOTER_COLORS[variant];

  return (
    <footer className="w-full">
      {/* Main Footer - 4 Column Layout */}
      <div className="text-white transition-colors duration-500" style={{ backgroundColor: scheme.primary }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Column 1 - Logo */}
            <div className="text-center sm:text-left flex flex-col items-center justify-start">
              <Link href="/">
                <Image
                  src="/design-images/logos/castle-hotel-daniel.svg"
                  alt="Castle Hotel Daniel"
                  width={109}
                  height={144}
                  className="h-36 w-auto"
                />
              </Link>
            </div>

            {/* Column 2 - Reception */}
            <div className="text-center sm:text-left">
              <h3 className="text-4xl font-serif  mb-6">{t('footer.reception')}</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <a href={`tel:${HOTEL_PHONE_TEL}`} className="hover:underline">{HOTEL_PHONE_DISPLAY}</a>
                </div>
                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <a href={`mailto:${HOTEL_EMAIL}`} className="hover:underline">{HOTEL_EMAIL}</a>
                </div>
                <div className="mt-4 text-white/80">
                  <p className="font-medium">{t('footer.officeHours')}</p>
                  <p>8:00 - 22:00</p>
                </div>
                <div className="mt-4 text-white/80 space-y-1">
                  <p><span className="font-medium">{t('footer.checkIn')}</span> 15:00</p>
                  <p><span className="font-medium">{t('footer.checkOut')}</span> 11:00</p>
                </div>
              </div>
            </div>

            {/* Columns 2 & 3 - Two-column nav on mobile, separate grid items on sm+ */}
            <div className="grid grid-cols-2 gap-8 sm:contents">
              {/* Column 2 - Site Navigation */}
              <div className="text-center sm:text-left flex justify-center sm:justify-start">
                <nav className="space-y-3 text-sm inline-block text-left">
                  <Link href="/about-us" className="block hover:underline underline-offset-4">{t('footer.aboutUs')}</Link>
                  <Link href="/rooms" className="block hover:underline underline-offset-4">{t('footer.rooms')}</Link>
                  <Link href="/dining" className="block hover:underline underline-offset-4">{t('footer.dining')}</Link>
                  <Link href="/spa" className="block hover:underline underline-offset-4">{t('footer.cellariumSpa')}</Link>
                  <Link href="/activities" className="block hover:underline underline-offset-4">{t('footer.activities')}</Link>
                  <Link href="/events" className="block hover:underline underline-offset-4">{t('footer.events')}</Link>
                  <Link href="/corporate" className="block hover:underline underline-offset-4">{t('footer.corporate')}</Link>
                  <Link href="/vouchers-and-offers" className="block hover:underline underline-offset-4">{t('footer.newsAndOffers')}</Link>
                </nav>
              </div>

              {/* Column 3 - Legal & Info */}
              <div className="text-center sm:text-left flex justify-center sm:justify-start">
                <nav className="space-y-3 text-sm inline-block text-left">
                  <Link href="/contact" className="block hover:underline underline-offset-4">{t('footer.contact')}</Link>
                  <Link href="/newsletter" className="block hover:underline underline-offset-4">{t('footer.newsletter')}</Link>
                  <Link href="/terms" className="block hover:underline underline-offset-4">{t('footer.termsConditions')}</Link>
                  <Link href="/privacy" className="block hover:underline underline-offset-4">{t('footer.privacyPolicy')}</Link>
                  <Link href="/cookies" className="block hover:underline underline-offset-4">{t('footer.cookiesPolicy')}</Link>
                  <CookieSettingsLink className="block text-left hover:underline underline-offset-4 cursor-pointer" />
                  <Link href="/faq" className="block hover:underline underline-offset-4">{t('footer.faq')}</Link>
                  <Link href="/how-to-find-us" className="block hover:underline underline-offset-4">{t('footer.howToFindUs')}</Link>
                  <Link href="/gift-vouchers" className="block hover:underline underline-offset-4">{t('footer.giftVouchers')}</Link>
                </nav>
              </div>
            </div>

            {/* Column 4 - Social Media & CTA */}
            <div className="text-center sm:text-left">
              {/* Social Icons - White/Transparent */}
              <div className="grid grid-cols-2 gap-3 max-w-[120px] mx-auto sm:mx-0">
                <a
                  href="https://www.facebook.com/DanielCastleHotel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/20 hover:border-white/50 transition-all"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/castlehoteldanieltransylvania/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/20 hover:border-white/50 transition-all"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@castlehoteldaniel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/20 hover:border-white/50 transition-all"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/company/castle-hotel-daniel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/20 hover:border-white/50 transition-all"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/channel/UCGw2uNahsBZL_LbbkyuwH1Q"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white/10 border border-white/30 rounded-lg flex items-center justify-center hover:bg-white/20 hover:border-white/50 transition-all"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Section - Partners, Payments, Directions */}
      <div className="text-white transition-colors duration-500" style={{ backgroundColor: scheme.secondary }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Partner Logos */}
            <div className="text-center">
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/70">{t('footer.ourPartners')}</h4>
              <div className="flex flex-wrap items-center justify-center gap-6">
                <a
                  href="https://www.booking.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 opacity-80 hover:opacity-100"
                >
                  <Image
                    src="/images/footer/booking-logo.png"
                    alt="Booking.com"
                    width={100}
                    height={32}
                    className="h-8 w-auto"
                  />
                </a>
                <a
                  href="https://www.tripadvisor.co.uk/Hotel_Review-g7097396-d7036985-Reviews-Castle_Hotel_Daniel-Talisoara_Covasna_County_Central_Romania_Transylvania.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform hover:scale-105 opacity-80 hover:opacity-100"
                >
                  <Image
                    src="/images/footer/tripadvisor-logo.png"
                    alt="TripAdvisor"
                    width={100}
                    height={32}
                    className="h-8 w-auto"
                  />
                </a>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="text-center">
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/70">{t('footer.weAccept')}</h4>
              <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/images/footer/footer-mastercard-logo.png"
                    alt="Mastercard"
                    width={65}
                    height={32}
                    className="h-7 w-auto"
                  />
                </div>
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/images/footer/footer-visa-logo.jpg"
                    alt="Visa"
                    width={50}
                    height={32}
                    className="h-7 w-auto"
                  />
                </div>
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/design-images/uploads/1781263041452-hhuqxn-maestro_2016.svg"
                    alt="Maestro"
                    width={50}
                    height={39}
                    className="h-7 w-auto"
                  />
                </div>
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/design-images/uploads/1781263379964-2na1yq-visa_electron.svg"
                    alt="Visa Electron"
                    width={50}
                    height={32}
                    className="h-7 w-auto"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/design-images/uploads/1781263814679-cm519a-up-romania.png"
                    alt="Up Romania"
                    width={50}
                    height={32}
                    className="h-7 w-auto"
                  />
                </div>
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/design-images/uploads/1781263814413-vso9hm-pluxee.png"
                    alt="Pluxee"
                    width={50}
                    height={32}
                    className="h-7 w-auto"
                  />
                </div>
                <div className="bg-white px-3 py-1 rounded">
                  <Image
                    src="/design-images/uploads/1781263814145-mcyitj-edenred-vacanta-card.png"
                    alt="Edenred"
                    width={50}
                    height={32}
                    className="h-7 w-auto"
                  />
                </div>
              </div>
            </div>

            {/* Directions Summary */}
            <div className="text-center lg:text-left">
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4 text-white/70">{t('footer.findUs')}</h4>
              <address className="not-italic text-sm text-white/80 space-y-1">
                <p className="font-medium text-white">{t('footer.address')}</p>
                <p>{t('footer.country')}</p>
                <p className="mt-2 text-xs text-white/60">
                  {t('footer.directions')}
                </p>
              </address>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="text-white/60 text-center text-sm py-4 transition-colors duration-500" style={{ backgroundColor: scheme.tertiary }}>
        <div className="max-w-7xl mx-auto px-4">
          {t('footer.copyright', { year: currentYear })}
          <span className="block mt-1 text-[10px] text-white/20">v1.6.1</span>
        </div>
      </div>
    </footer>
  );
}
